<ng-container *transloco="let t; read: 'expenses'">
  <form
    [formGroup]="filterForm"
    (ngSubmit)="onSubmitFilter()"
    class="flex flexDirRow justSpaceBetween twoCol"
  >
    <div class="flexItem mbs">
      <label for="accountingEmployee">{{ t('filterEmployee') }}</label>
      <kendo-dropdownlist
        formControlName="employee"
        id="accountingEmployee"
        [data]="employees"
        textField="lastName"
        valueField="id"
        [valuePrimitive]="false"
        [defaultItem]="{ lastName: '', id: null }"
      >
        <ng-template kendoDropDownListItemTemplate let-dataItem>
          <ng-container *ngIf="dataItem.lastName">
            {{ dataItem.firstName }} {{ dataItem.lastName }}
          </ng-container>
          <ng-container *ngIf="!dataItem.lastName">
            {{ t('filterAllEmployees') }}
          </ng-container>
        </ng-template>
        <ng-template kendoDropDownListValueTemplate let-dataItem>
          <ng-container *ngIf="dataItem.lastName">
            {{ dataItem?.firstName }} {{ dataItem?.lastName }}
          </ng-container>
          <ng-container *ngIf="!dataItem.lastName">
            {{ t('filterAllEmployees') }}
          </ng-container>
        </ng-template>
      </kendo-dropdownlist>
    </div>

    <div class="flexItem mbs">
      <label for="accountingStatus">{{ t('filterStatus') }}</label>
      <kendo-dropdownlist
        formControlName="status"
        id="accountingStatus"
        [data]="statusFilterItems"
        [defaultItem]="allStatusesItem"
        textField="text"
        valueField="value"
        [valuePrimitive]="true"
      ></kendo-dropdownlist>
    </div>

    <div class="flexItem mbs">
      <kendo-daterange>
        <kendo-floatinglabel [text]="t('filterDateFrom')" class="mrs">
          <kendo-dateinput
            formControlName="dateRangeStart"
            kendoDateRangeStartInput
          ></kendo-dateinput>
        </kendo-floatinglabel>
        <kendo-floatinglabel [text]="t('filterDateTo')">
          <kendo-dateinput
            formControlName="dateRangeEnd"
            kendoDateRangeEndInput
          ></kendo-dateinput>
        </kendo-floatinglabel>
        <kendo-daterange-popup [animate]="false"></kendo-daterange-popup>
      </kendo-daterange>
    </div>

    <div class="flexItem">
      <button
        type="submit"
        kendoButton
        themeColor="primary"
        class="mbs mrs"
        loadingIndication="/Expenses/SearchAccountingExpenses"
      >
        {{ t('filterSearch') }}
      </button>

      <button
        type="button"
        kendoButton
        themeColor="secondary"
        (click)="onResetFilter()"
        class="mbs"
        loadingIndication="/Expenses/SearchAccountingExpenses"
      >
        {{ t('filterReset') }}
      </button>
    </div>
  </form>

  <!-- Row actions, shared by the desktop action column and the mobile card.
       Paid is terminal: only accepted rows can still be paid out or rejected. -->
  <ng-template #rowActions let-dataItem>
    <button kendoButton class="mbs mrs" (click)="open(dataItem)">{{ t('view') }}</button>
    <ng-container *ngIf="dataItem.status === ExpenseStatus.Accepted">
      <button kendoButton themeColor="success" class="mbs mrs" (click)="markPaid(dataItem)">{{ t('markPaid') }}</button>
      <button kendoButton themeColor="error" class="mbs mrs" (click)="openReject(dataItem)">{{ t('reject') }}</button>
    </ng-container>
  </ng-template>

  <kendo-grid
    [data]="expenses"
    [skip]="gridState.skip"
    [pageSize]="gridState.pageSize"
    [pageable]="true"
    (pageChange)="onPageChange($event)"
    scrollable="none"
    [selectable]="{ enabled: true, checkboxOnly: true, mode: 'multiple' }"
    [kendoGridSelectBy]="'id'"
    [(selectedKeys)]="selectedIds"
    [sortable]="{ allowUnsort: false }"
    [sort]="sort"
    (sortChange)="onSortChange($event)"
  >
    <kendo-grid-messages [pagerOf]="t('pagerOf')" [pagerItems]="t('pagerItems')"
                         [noRecords]="t('noRecords')">
    </kendo-grid-messages>

    <ng-template kendoGridToolbarTemplate>
      <button
        kendoButton
        themeColor="success"
        [disabled]="!selectedAcceptedIds.length"
        (click)="markPaidSelected()"
        loadingIndication="/Expenses/MarkExpensePaid">
        {{ t('markPaidSelected') }}
      </button>
      <kendo-dropdownbutton
        [data]="exportFormatItems"
        textField="text"
        themeColor="primary"
        [disabled]="!selectedIds.length"
        (itemClick)="exportSelected($event)"
        loadingIndication="/Expenses/ExportExpenses">
        {{ t('exportSelected') }}
      </kendo-dropdownbutton>
      <kendo-dropdownbutton
        [data]="exportFormatItems"
        textField="text"
        (itemClick)="exportAll($event)"
        loadingIndication="/Expenses/ExportExpenses">
        {{ t('exportAll') }}
      </kendo-dropdownbutton>
    </ng-template>

    <!-- The checkbox column stays on every screen size: bulk payout and export need it on phones too. -->
    <kendo-grid-checkbox-column [width]="40" [showSelectAll]="true"
                                class="expense-checkbox-col" headerClass="expense-checkbox-col">
    </kendo-grid-checkbox-column>

    <kendo-grid-column field="date" [title]="t('date')" [width]="120" media="(min-width: 450px)">
      <ng-template kendoGridCellTemplate let-dataItem>
        {{ dataItem.date | date:'dd.MM.yyyy' }}
      </ng-template>
    </kendo-grid-column>

    <!-- field is needed for the sortable header; the sort is mapped to Submitter.LastName/FirstName -->
    <kendo-grid-column field="submitterLastName" [title]="t('submitter')" media="(min-width: 450px)">
      <ng-template kendoGridCellTemplate let-dataItem>
        {{ dataItem.submitterFirstName }} {{ dataItem.submitterLastName }}
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column field="expenseTypeName" [title]="t('expenseType')" [sortable]="false"
                       media="(min-width: 450px)"></kendo-grid-column>

    <kendo-grid-column field="amount" [title]="t('amount')" [width]="120" [sortable]="false"
                       media="(min-width: 450px)">
      <ng-template kendoGridCellTemplate let-dataItem>
        CHF {{ dataItem.amount | number:'1.2-2' }}
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column field="expenseTypeSalaryPayment" [title]="t('salaryPayment')" [width]="120" [sortable]="false"
                       media="(min-width: 450px)">
      <ng-template kendoGridCellTemplate let-dataItem>
        {{ dataItem.expenseTypeSalaryPayment ? t('yes') : t('no') }}
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column field="status" [title]="t('status')" [width]="130" [sortable]="false"
                       media="(min-width: 450px)">
      <ng-template kendoGridCellTemplate let-dataItem>
        <span class="status-badge" [ngClass]="statusClass(dataItem.status)">
          {{ t('expenseStates.' + statusName(dataItem.status)) }}
        </span>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-command-column [title]="t('action')" [width]="320" media="(min-width: 450px)">
      <ng-template kendoGridCellTemplate let-dataItem>
        <ng-container *ngTemplateOutlet="rowActions; context: { $implicit: dataItem }"></ng-container>
      </ng-template>
    </kendo-grid-command-column>

    <!-- Mobile view: one card per expense instead of the columns above -->
    <kendo-grid-column media="(max-width: 450px)" [title]="t('entries')">
      <ng-template kendoGridCellTemplate let-dataItem>
        <h4 class="mbs">
          {{ dataItem.date | date:'dd.MM.yyyy' }} &middot; CHF {{ dataItem.amount | number:'1.2-2' }}
        </h4>

        <dl>
          <dt>{{ t('submitter') }}</dt>
          <dd>{{ dataItem.submitterFirstName }} {{ dataItem.submitterLastName }}</dd>
        </dl>
        <dl>
          <dt>{{ t('expenseType') }}</dt>
          <dd>{{ dataItem.expenseTypeName }}</dd>
        </dl>
        <dl>
          <dt>{{ t('salaryPayment') }}</dt>
          <dd>{{ dataItem.expenseTypeSalaryPayment ? t('yes') : t('no') }}</dd>
        </dl>
        <dl>
          <dt>{{ t('status') }}</dt>
          <dd>
            <span class="status-badge" [ngClass]="statusClass(dataItem.status)">
              {{ t('expenseStates.' + statusName(dataItem.status)) }}
            </span>
          </dd>
        </dl>

        <ng-container *ngTemplateOutlet="rowActions; context: { $implicit: dataItem }"></ng-container>
      </ng-template>
    </kendo-grid-column>

    <ng-template kendoPagerTemplate>
      <div class="flex flexDirRow">
        <kendo-pager-prev-buttons></kendo-pager-prev-buttons>
        <kendo-pager-numeric-buttons [buttonCount]="5"></kendo-pager-numeric-buttons>
        <kendo-pager-next-buttons></kendo-pager-next-buttons>
      </div>
      <div>
        <label for="accountingPageBtnGroup" class="mrs">{{ t('resultsPerPage') }}</label>
        <kendo-buttongroup [selection]="'single'" id="accountingPageBtnGroup">
          <button (click)="onPageSizeChange(pageSizes.Small)" kendoButton
                  [selected]="gridState.pageSize === pageSizes.Small" [toggleable]="true">
            {{ pageSizes.Small }}
          </button>
          <button (click)="onPageSizeChange(pageSizes.Medium)" kendoButton
                  [selected]="gridState.pageSize === pageSizes.Medium" [toggleable]="true">
            {{ pageSizes.Medium }}
          </button>
          <button (click)="onPageSizeChange(pageSizes.Large)" kendoButton
                  [selected]="gridState.pageSize === pageSizes.Large" [toggleable]="true">
            {{ pageSizes.Large }}
          </button>
        </kendo-buttongroup>
        <button kendoButton [title]="t('refresh')" (click)="onGridReload()" icon="reload"></button>
      </div>
    </ng-template>
  </kendo-grid>

  <app-expense-dialog
    *ngIf="dialogOpen"
    [expenseId]="dialogId"
    [editable]="false"
    (close)="closeDialog()">
  </app-expense-dialog>

  <app-expense-reason-dialog
    *ngIf="rejectOpen && rejectId"
    titleKey="reject"
    confirmKey="reject"
    (confirm)="confirmReject($event)"
    (cancel)="closeReject()">
  </app-expense-reason-dialog>
</ng-container>
